/**
 * String Utility Functions
 * Collection of reusable string formatting and manipulation functions
 */

/**
 * Capitalizes the first letter of every word in a string
 * @param {string} str - The input string to capitalize
 * @returns {string} - The string with first letter of each word capitalized
 *
 * @example
 * capitalizeWords("john doe") // returns "John Doe"
 * capitalizeWords("MARY JANE") // returns "Mary Jane"
 * capitalizeWords("robert smith jr") // returns "Robert Smith Jr"
 */
export const capitalizeWords = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Capitalizes only the first letter of a string
 * @param {string} str - The input string to capitalize
 * @returns {string} - The string with first letter capitalized
 *
 * @example
 * capitalizeFirst("hello world") // returns "Hello world"
 * capitalizeFirst("WORLD") // returns "World"
 */
export const capitalizeFirst = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

/**
 * Converts a string to title case (first letter of each word capitalized)
 * @param {string} str - The input string to convert
 * @returns {string} - The string in title case
 *
 * @example
 * toTitleCase("hello world") // returns "Hello World"
 * toTitleCase("the quick brown fox") // returns "The Quick Brown Fox"
 */
export const toTitleCase = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Removes extra spaces and normalizes string formatting
 * @param {string} str - The input string to normalize
 * @returns {string} - The normalized string
 *
 * @example
 * normalizeString("  hello   world  ") // returns "hello world"
 * normalizeString("  JOHN    DOE  ") // returns "john doe"
 */
export const normalizeString = (str) => {
  if (!str || typeof str !== 'string') return str;
  return str.trim().replace(/\s+/g, ' ');
};

/**
 * Formats a string with proper capitalization and spacing
 * @param {string} str - The input string to format
 * @returns {string} - The formatted string
 *
 * @example
 * formatName("  john   doe  ") // returns "John Doe"
 * formatName("MARY JANE") // returns "Mary Jane"
 */
export const formatName = (str) => {
  if (!str || typeof str !== 'string') return str;
  const normalized = normalizeString(str);
  return capitalizeWords(normalized);
};

/**
 * Formats status/state values for display (e.g. not_interested → Not Interested)
 * @param {string} str - Raw status value
 * @returns {string} - Title-cased label with underscores/hyphens as spaces
 */
export const formatStatusLabel = (str) => {
  if (!str || typeof str !== 'string') return str;
  return capitalizeWords(str.replace(/[_-]+/g, ' ').trim());
};

/**
 * Truncates a string to a specified length with ellipsis
 * @param {string} str - The input string to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} - The truncated string
 *
 * @example
 * truncateString("Hello world", 8) // returns "Hello..."
 * truncateString("Short", 10) // returns "Short"
 */
export const truncateString = (str, maxLength = 50) => {
  if (!str || typeof str !== 'string') return str;
  if (str.length <= maxLength) return str;
  return str.slice(0, maxLength) + '...';
};
